'use client'

import { getUser } from '@/features/Auth/auth.hooks'
import { createCardsQueryOptions } from '@/features/Cards/queries'
import { extractDomainPath } from '@/shared/helpers/url'
import { ReactNode, useEffect } from 'react'
import useWebSocket from 'react-use-websocket'
import { useNotificationsStore } from '@/store/notifications/notifications.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TransactionData, TransactionStatus } from '@/features/Transactions/transactions.types'
import { balancesQueryKey } from '@/features/Wallets/queries'

interface LastJsonMessage {
  payload: unknown;
  type: string;
}

const typesWhitelist = [
  'TEXT_NOTIFICATION',
  'BALANCE_UPDATE',
  'TRANSACTION_DETAILS_UPDATE',
]

export function SocketProvider({ children }: { children: ReactNode }) {
  const user = getUser()
  const updateNotifications = useNotificationsStore((state) => state.setIsNewExists)
  const queryClient = useQueryClient()
  const { data: cards, refetch: updateCards } = useQuery(createCardsQueryOptions(!!user))

  const { lastJsonMessage } = useWebSocket(`wss://${extractDomainPath(process.env.NEXT_PUBLIC_API_URL)}/ws/notifications?token=${user?.accessToken}`, {
    shouldReconnect: () => true,
    onOpen: () => {
      console.info('WS: opened')
    },
    onClose: () => {
      console.info('WS: closed')
    },
    onError: (event) => {
      console.error('WS: error', event)
    },
    filter: (msg) => {
      const messageParsed = JSON.parse(msg.data)
      return typesWhitelist.includes(messageParsed.type)
    },
    share: true,
  }, !!user)

  useEffect(() => {
    if (lastJsonMessage) {
      const message = lastJsonMessage as LastJsonMessage
      if (message.type === 'BALANCE_UPDATE') {
        const payload = message.payload as { address?: string }
        queryClient.invalidateQueries({ queryKey: balancesQueryKey })

        const shouldUpdateCards = cards?.some((card) => card.solanaAddress === payload?.address)
        if (shouldUpdateCards) updateCards().then(() => {/*empty*/})
      }

      if (message.type === 'TRANSACTION_DETAILS_UPDATE') {
        const txUpdate = message.payload as {id: string; status: string}
        const newStatus = txUpdate.status as TransactionStatus

        queryClient.setQueriesData<TransactionData[]>(
          { queryKey: ['transactions'] },
          (old) => {
            if (!old) return old
            const index = old.findIndex((tx) => tx.id === txUpdate.id)
            if (index === -1) return old
            if (old[index].status === newStatus) return old
            const updated = [...old]
            updated[index] = { ...updated[index], status: newStatus }
            return updated
          }
        )

        queryClient.setQueriesData(
          { queryKey: ['transactions', 'infinite'] },
          (old: { pages: TransactionData[][] } | undefined) => {
            if (!old?.pages) return old
            let hasChanges = false
            const updatedPages = old.pages.map((page) => {
              const index = page.findIndex((tx) => tx.id === txUpdate.id)
              if (index === -1) return page
              if (page[index].status === newStatus) return page
              hasChanges = true
              const updatedPage = [...page]
              updatedPage[index] = { ...updatedPage[index], status: newStatus }
              return updatedPage
            })
            return hasChanges ? { ...old, pages: updatedPages } : old
          }
        )

        queryClient.setQueryData<TransactionData>(
          ['transactionDetails', txUpdate.id],
          (old) => {
            if (!old || old.status === newStatus) return old
            return { ...old, status: newStatus }
          }
        )

        queryClient.invalidateQueries({ queryKey: ['transactions'] })
      }

      if (message.type === 'TEXT_NOTIFICATION') {
        updateNotifications(true)
      }
    }
  }, [lastJsonMessage, updateNotifications, queryClient, cards, updateCards])

  return children
}
